import { Component, OnInit, inject } from '@angular/core';
import { NgIf } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
    IdentityBrokeringPolicyDto,
    MultiSelectComponent,
    SingleSignOnServiceService,
    SsoUserAttributeTypeDto,
    SsoUserAttributeTypeService,
    EnterpriseLabels,
    getEnterpriseLabels,
    SecurityDomain
} from 'lib';
import { catchError, forkJoin, of } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute, Router } from '@angular/router';
import { EnterpriseLabelsFetchService } from '../../services/enterprise-labels-fetch.service';

@Component({
    selector: 'mmoga-single-sign-on',
    templateUrl: './single-sign-on.component.html',
    standalone: true,
    imports: [
        NgIf,
        ReactiveFormsModule,
        MultiSelectComponent
    ],
    styleUrl: './single-sign-on.component.scss'
})
export class SingleSignOnComponent implements OnInit {
    private readonly ssoPolicyService = inject(SingleSignOnServiceService);
    private readonly ssoUserAttributeTypeService = inject(SsoUserAttributeTypeService);
    private readonly toastrService = inject(ToastrService);
    private readonly activatedRoute = inject(ActivatedRoute);
    private router = inject(Router);
    private fb = inject(FormBuilder);
    private readonly enterpriseLabelsFetchService = inject(EnterpriseLabelsFetchService);

    ssoLoginForm: FormGroup;
    showLoginButtonText = false;
    showLogoutUrlText = false;
    enterpriseId = 0;
    enterpriseLabels: EnterpriseLabels = getEnterpriseLabels(SecurityDomain.MODMED);
    uniqueIdentifierOptions: SsoUserAttributeTypeDto[] = [];
    hasMissingEmail = true;
    hasMissingDob = true;

    constructor() {
        this.ssoLoginForm = this.getSsoLoginForm();
    }

    ngOnInit(): void {
        this.fetchIdpPolicy();
        this.fetchIdpAttributes();
        this.setUpFormReactions();
        this.enterpriseLabelsFetchService.getEnterpriseLabels().then(
            labels => this.enterpriseLabels = labels
        );
    }

    private getSsoLoginForm() {
        return this.fb.group({
            idpEnabled: [false],
            attributes: [[]],
            storePasswords: [true],
            loginPage: [false],
            externalIdpAlias: ['', [Validators.required, Validators.pattern(/\S+/)]],
            logoutPage: [false],
            loginButtonText: [''],
            externalLogoutUrl: [''],
            emailRequired: [true],
            dobRequired: [true]
        });
    }

    private setUpFormReactions() {
        this.ssoLoginForm.get('loginPage')?.valueChanges.subscribe((value: boolean) => {
            this.showLoginButtonText = value;
            this.updateTextControlState('loginButtonText', value);
        });
        this.ssoLoginForm.get('logoutPage')?.valueChanges.subscribe((value: boolean) => {
            this.showLogoutUrlText = value;
            this.updateTextControlState('externalLogoutUrl', value);
        });
        this.ssoLoginForm.get('attributes')?.valueChanges.subscribe((selectedNames: string[]) => {
            this.enforceRequiredFlags(selectedNames);
        });
        this.ssoLoginForm.get('idpEnabled')?.valueChanges.subscribe(() => {
            this.enforceRequiredFlags(this.ssoLoginForm.get('attributes')?.value ?? []);
        });
        this.enforceRequiredFlags(this.ssoLoginForm.get('attributes')?.value ?? []);
    }

    private enforceRequiredFlags(selectedNames: string[]): void {
        const emailControl = this.ssoLoginForm.get('emailRequired');
        const dobControl = this.ssoLoginForm.get('dobRequired');
        const idpEnabled = this.ssoLoginForm.get('idpEnabled')?.value;

        if (!idpEnabled) {
            emailControl?.setValue(this.hasMissingEmail ? false : true, { emitEvent: false });
            emailControl?.disable({ emitEvent: false });
            dobControl?.setValue(this.hasMissingDob ? false : true, { emitEvent: false });
            dobControl?.disable({ emitEvent: false });
            return;
        }

        if (this.hasMissingEmail) {
            emailControl?.setValue(false, { emitEvent: false });
            emailControl?.disable({ emitEvent: false });
        } else if (selectedNames.includes('email')) {
            emailControl?.setValue(true, { emitEvent: false });
            emailControl?.disable({ emitEvent: false });
        } else {
            emailControl?.enable({ emitEvent: false });
        }

        if (this.hasMissingDob) {
            dobControl?.setValue(false, { emitEvent: false });
            dobControl?.disable({ emitEvent: false });
        } else if (selectedNames.includes('dateOfBirth')) {
            dobControl?.setValue(true, { emitEvent: false });
            dobControl?.disable({ emitEvent: false });
        } else {
            dobControl?.enable({ emitEvent: false });
        }
    }

    private fetchIdpPolicy() {
        this.activatedRoute.parent?.params.subscribe(params => {
            this.enterpriseId = params[ 'id' ];
            forkJoin({
                idpPolicy: this.ssoPolicyService.getIdentityBrokeringPolicy(this.enterpriseId)?.pipe(
                    catchError(() => {
                        this.toastrService.error('Failed to load External Single Sign-on policy', 'Error');
                        return of(null);
                    })
                ) ?? of(null),
                hasMissingEmail: this.ssoPolicyService.hasMissingEmail(this.enterpriseId).pipe(catchError(() => of(false))),
                hasMissingDob: this.ssoPolicyService.hasMissingDateOfBirth(this.enterpriseId).pipe(catchError(() => of(false)))
            }).subscribe(({ idpPolicy: idpPolicyDto, hasMissingEmail, hasMissingDob }) => {
                this.hasMissingEmail = hasMissingEmail;
                this.hasMissingDob = hasMissingDob;

                const attributeMappings = idpPolicyDto?.attributeMappings || {};
                const selectedAttributeNames = Object.keys(attributeMappings);

                this.ssoLoginForm.patchValue({
                    externalIdpAlias: idpPolicyDto?.alias,
                    idpEnabled: idpPolicyDto?.active,
                    loginPage: idpPolicyDto?.idpLink || false,
                    loginButtonText: idpPolicyDto?.idpLoginText,
                    logoutPage: idpPolicyDto?.frontChannelLogout || false,
                    externalLogoutUrl: idpPolicyDto?.frontChannelLogoutUrl,
                    storePasswords: idpPolicyDto?.storePasswords,
                    attributes: selectedAttributeNames,
                    emailRequired: idpPolicyDto?.emailRequired ?? true,
                    dobRequired: idpPolicyDto?.dobRequired ?? true
                });
                this.enforceRequiredFlags(selectedAttributeNames);
            });
        });
    }

    private fetchIdpAttributes() {
        this.ssoUserAttributeTypeService.getIdpAttributes().pipe(
            catchError(() => {
                this.toastrService.error('Failed to load IDP attributes', 'Error');
                return of([]);
            })
        ).subscribe(attributes => {
            this.uniqueIdentifierOptions = attributes;
        });
    }

    private saveIdpPolicy() {
        const idpPolicyDto = this.getIdentityBrokeringPolicyDto();
        this.ssoPolicyService.saveIdentityBrokeringPolicy(this.enterpriseId, idpPolicyDto).subscribe({
            next: () => {
                this.toastrService.success('External Single Sign-on policy updated', 'Single Sign - on');
            },
            error: (error) => {
                const errorMessage = error?.error?.message || 'There was an error when trying to update External Single Sign-on.';
                this.toastrService.error(errorMessage, 'Single Sign - on');
            }
        });
    }

    private getIdentityBrokeringPolicyDto(): IdentityBrokeringPolicyDto {
        const selectedNames = this.ssoLoginForm.get('attributes')?.value as string[] || [];
        const selectedAttributes = this.uniqueIdentifierOptions.filter(attr => selectedNames.includes(attr.name));
        const attributeMappings: Record<string, string> = {};
        selectedAttributes.forEach(attr => {
            attributeMappings[attr.name] = attr.claim;
        });

        return {
            active: this.ssoLoginForm.get('idpEnabled')?.value as unknown as boolean,
            alias: this.ssoLoginForm.get('externalIdpAlias')?.value as unknown as string,
            idpLink: this.ssoLoginForm.get('loginPage')?.value as unknown as boolean,
            idpLoginText: this.ssoLoginForm.get('loginButtonText')?.value as unknown as string,
            frontChannelLogout: this.ssoLoginForm.get('logoutPage')?.value as unknown as boolean,
            frontChannelLogoutUrl: this.ssoLoginForm.get('externalLogoutUrl')?.value as unknown as string,
            storePasswords: this.ssoLoginForm.get('storePasswords')?.value as unknown as boolean,
            linkOnAttribute: selectedAttributes.length > 0,
            attributeMappings,
            emailRequired: this.ssoLoginForm.get('emailRequired')?.value as unknown as boolean,
            dobRequired: this.ssoLoginForm.get('dobRequired')?.value as unknown as boolean
        };
    }

    private updateTextControlState(formName: string, enabled: boolean): void {
        const formControl = this.ssoLoginForm.get(formName);
        if (enabled) {
            formControl?.setValidators([Validators.required, Validators.pattern(/\S+/)]);
        } else {
            formControl?.setValue('');
            formControl?.clearValidators();
        }
        formControl?.updateValueAndValidity({ emitEvent: false });
    }

    save() {
        this.saveIdpPolicy();
    }

    cancel() {
        this.router.navigate(['/home/groups']);
    }

}
